import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { formatApiErrorDetail } from '../lib/api';
import PublicHeader from '../components/PublicHeader';
import StarRating from '../components/StarRating';
import { locName } from '../lib/constants';
import { useToast } from '../lib/toast';

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { show } = useToast();
  const [params] = useSearchParams();
  const paymentCancelled = params.get('payment') === 'cancelled';
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgErr, setImgErr] = useState(false);
  const [blockedRanges, setBlockedRanges] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [booking, setBooking] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    pickup_location: '',
    dropoff_location: '',
    pickup_date: '',
    dropoff_date: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api
      .get(`/vehicles/${id}`)
      .then((r) => {
        setCar(r.data);
        const firstLoc = (r.data.locs || [])[0] || '';
        setBooking((b) => ({ ...b, pickup_location: firstLoc, dropoff_location: firstLoc }));
      })
      .catch(() => setCar(false))
      .finally(() => setLoading(false));
    api
      .get(`/vehicles/${id}/availability`)
      .then((r) => setBlockedRanges(r.data.blocked_ranges || []))
      .catch(() => { /* non-fatal */ });
    api
      .get(`/reviews?vid=${id}`)
      .then((r) => setReviews(r.data || []))
      .catch(() => { /* non-fatal */ });
  }, [id]);

  const today = new Date().toISOString().split('T')[0];
  const localConflict = (() => {
    if (!booking.pickup_date || !booking.dropoff_date) return null;
    return blockedRanges.find((r) => booking.pickup_date < r.to && booking.dropoff_date > r.from) || null;
  })();

  const days = (() => {
    if (!booking.pickup_date || !booking.dropoff_date) return 0;
    const d1 = new Date(booking.pickup_date);
    const d2 = new Date(booking.dropoff_date);
    const diff = Math.ceil((d2 - d1) / 86400000);
    return diff > 0 ? diff : 0;
  })();
  const subtotal = days * (car?.ppd || 0);
  const total = subtotal + (car?.dep || 0);

  async function submitBooking(e) {
    e.preventDefault();
    if (localConflict) {
      show(`Selected dates overlap with an existing booking (${localConflict.from} → ${localConflict.to}). Pick different dates.`, 'error');
      return;
    }
    setSubmitting(true);
    try {
      const { data: bk } = await api.post('/bookings', { vid: id, ...booking });
      // Immediately create Stripe checkout session
      const { data: ck } = await api.post('/payments/checkout-session', {
        booking_id: bk.id,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe
      window.location.href = ck.url;
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || 'Booking failed';
      show(detail, 'error');
      // If 409 (conflict), refresh availability so user sees up-to-date blocked ranges
      if (status === 409) {
        try {
          const { data } = await api.get(`/vehicles/${id}/availability`);
          setBlockedRanges(data.blocked_ranges || []);
        } catch (_e) { /* ignore — error toast already shown */ }
      }
      setSubmitting(false);
    }
  }

  if (loading) return <div style={{ padding: 80, textAlign: 'center', color: '#94A3B8' }}>Loading…</div>;
  if (car === false)
    return (
      <div>
        <PublicHeader />
        <div style={{ padding: 80, textAlign: 'center' }}>
          <h2 style={{ fontSize: 24, color: '#0F172A' }}>Vehicle not found</h2>
          <Link to="/" style={{ color: '#E63946', marginTop: 16, display: 'inline-block' }}>
            ← Back to fleet
          </Link>
        </div>
      </div>
    );

  if (confirmation) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>
        <PublicHeader />
        <div style={{ maxWidth: 640, margin: '60px auto', padding: 24 }}>
          <div data-testid="booking-confirmation" className="card fade-up" style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 56 }}>✅</div>
            <h2 style={{ fontSize: 26, fontWeight: 900, marginTop: 12, color: '#0D1B3E' }}>Booking confirmed</h2>
            <p style={{ color: '#64748B', marginTop: 8 }}>
              Confirmation #<strong data-testid="booking-id">{confirmation.id}</strong> · {confirmation.vehicle_label}
            </p>
            <div style={{ marginTop: 24, padding: 18, background: '#F1F5F9', borderRadius: 12, textAlign: 'left' }}>
              <Row label="Pickup" value={`${locName(confirmation.pickup_location)} · ${confirmation.pickup_date}`} />
              <Row label="Drop-off" value={`${locName(confirmation.dropoff_location)} · ${confirmation.dropoff_date}`} />
              <Row label="Days" value={`${confirmation.days} × $${confirmation.price_per_day.toFixed(2)}`} />
              <Row label="Deposit" value={`$${confirmation.deposit.toFixed(2)}`} />
              <Row label="Total" value={`$${confirmation.total.toFixed(2)}`} bold />
            </div>
            <p style={{ color: '#64748B', fontSize: 13, marginTop: 20 }}>
              A confirmation has been sent to <strong>{confirmation.customer_email}</strong>.
            </p>
            <button onClick={() => nav('/')} className="btn-primary" style={{ marginTop: 22 }} data-testid="back-to-fleet-btn">
              Back to fleet
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <PublicHeader />
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '32px 24px' }}>
        <Link to="/" data-testid="back-link" style={{ color: '#64748B', fontSize: 13, fontWeight: 600 }}>
          ← Back to fleet
        </Link>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(280px,1fr)', gap: 36, marginTop: 18, alignItems: 'flex-start' }}>
          {/* Left: vehicle details */}
          <div>
            <div
              style={{
                height: 380,
                borderRadius: 18,
                background: `linear-gradient(135deg, ${car.color}22, ${car.color}44)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {car.img && !imgErr ? (
                <img src={car.img} alt={`${car.brand} ${car.model}`} onError={() => setImgErr(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: 180 }}>{car.emoji}</span>
              )}
            </div>

            <div style={{ marginTop: 24 }}>
              <span style={{ background: car.color + '22', color: car.color, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                {car.cat}
              </span>
              <h1 style={{ fontSize: 40, fontWeight: 900, color: '#0F172A', marginTop: 14, letterSpacing: '-.02em', lineHeight: 1.1 }}>
                {car.brand} {car.model}
              </h1>
              <p style={{ color: '#64748B', marginTop: 6, fontSize: 15 }}>
                {car.yr} · {car.trans} · {car.fuel} · {car.mpg} mpg
              </p>
              {(car.review_count > 0 || car.avg_rating) && (
                <div style={{ marginTop: 10 }}>
                  <StarRating value={car.avg_rating || 0} count={car.review_count || 0} size={16} />
                </div>
              )}
              {car.desc && <p style={{ color: '#475569', marginTop: 18, fontSize: 15, lineHeight: 1.6 }}>{car.desc}</p>}
            </div>

            <div style={{ marginTop: 26, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <Spec icon="👥" label="Seats" value={car.seats} />
              <Spec icon="🚪" label="Doors" value={car.doors} />
              <Spec icon="🧳" label="Luggage" value={`${car.bags} bags`} />
              <Spec icon="⛽" label="MPG" value={car.mpg} />
            </div>

            {car.feats && (
              <div style={{ marginTop: 30 }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', marginBottom: 12 }}>Features</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {car.feats
                    .split(',')
                    .filter((f) => f.trim())
                    .map((f, i) => (
                      <span key={i} style={{ background: '#fff', border: '1px solid #E2E8F0', color: '#0F172A', borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 600 }}>
                        ✓ {f.trim()}
                      </span>
                    ))}
                </div>
              </div>
            )}

            {(car.locs || []).length > 0 && (
              <div style={{ marginTop: 30 }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', marginBottom: 12 }}>Pickup locations</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(car.locs || []).map((id) => (
                    <span key={id} style={{ background: '#EFF6FF', color: '#1E40AF', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 600 }}>
                      📍 {locName(id)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {reviews.length > 0 && (
              <div data-testid="reviews-section" style={{ marginTop: 36 }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', marginBottom: 4 }}>What renters say</h3>
                <div style={{ marginBottom: 14 }}>
                  <StarRating value={car.avg_rating || 0} count={car.review_count || 0} size={14} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {reviews.slice(0, 6).map((r) => (
                    <div key={r.id} data-testid={`review-${r.id}`} className="card" style={{ padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <strong style={{ color: '#0F172A', fontSize: 14 }}>
                          {(r.customer_name || 'Anonymous').split(' ')[0]}
                          {(r.customer_name || '').split(' ')[1] && ' ' + (r.customer_name || '').split(' ')[1][0] + '.'}
                        </strong>
                        <span style={{ fontSize: 11, color: '#94A3B8' }}>
                          {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <StarRating value={r.rating} size={13} />
                      </div>
                      {r.comment && <p style={{ marginTop: 8, color: '#475569', fontSize: 14, lineHeight: 1.55 }}>{r.comment}</p>}
                    </div>
                  ))}
                </div>
                {reviews.length > 6 && (
                  <p style={{ marginTop: 10, color: '#94A3B8', fontSize: 12, textAlign: 'center' }}>+ {reviews.length - 6} more reviews</p>
                )}
              </div>
            )}
          </div>

          {/* Right: booking form */}
          <div data-testid="booking-card" className="card" style={{ position: 'sticky', top: 84, padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 36, fontWeight: 900, color: '#0D1B3E' }}>${car.ppd}</span>
              <span style={{ color: '#94A3B8' }}>/day</span>
            </div>
            <p style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>+ ${car.dep} refundable deposit</p>

            {car.status !== 'Available' ? (
              <div style={{ marginTop: 18, padding: 14, background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 10, color: '#92400E', fontSize: 13, fontWeight: 600 }}>
                ⚠️ This vehicle is currently <strong>{car.status}</strong> and unavailable for booking.
              </div>
            ) : (
              <form onSubmit={submitBooking} style={{ marginTop: 18 }}>
                {paymentCancelled && (
                  <div data-testid="payment-cancelled-banner" style={{ marginBottom: 14, padding: '10px 12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, color: '#92400E', fontSize: 12, fontWeight: 600 }}>
                    ⚠️ Payment was cancelled. Your reservation is still pending — finish checkout to confirm.
                  </div>
                )}
                <Inp label="Full name" required value={booking.customer_name} onChange={(v) => setBooking({ ...booking, customer_name: v })} testId="bk-name" />
                <Inp label="Email" type="email" required value={booking.customer_email} onChange={(v) => setBooking({ ...booking, customer_email: v })} testId="bk-email" />
                <Inp label="Phone" value={booking.customer_phone} onChange={(v) => setBooking({ ...booking, customer_phone: v })} testId="bk-phone" />
                <SelInp
                  label="Pickup location"
                  required
                  value={booking.pickup_location}
                  onChange={(v) => setBooking({ ...booking, pickup_location: v })}
                  options={(car.locs || []).map((l) => ({ v: l, l: locName(l) }))}
                  testId="bk-pickup-loc"
                />
                <SelInp
                  label="Drop-off location"
                  required
                  value={booking.dropoff_location}
                  onChange={(v) => setBooking({ ...booking, dropoff_location: v })}
                  options={(car.locs || []).map((l) => ({ v: l, l: locName(l) }))}
                  testId="bk-dropoff-loc"
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Inp label="Pickup date" type="date" required value={booking.pickup_date} onChange={(v) => setBooking({ ...booking, pickup_date: v })} testId="bk-pickup-date" min={today} />
                  <Inp label="Drop-off date" type="date" required value={booking.dropoff_date} onChange={(v) => setBooking({ ...booking, dropoff_date: v })} testId="bk-dropoff-date" min={booking.pickup_date || today} />
                </div>

                {blockedRanges.length > 0 && (
                  <div data-testid="blocked-dates" style={{ marginTop: -4, marginBottom: 12, padding: 10, background: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 11, color: '#713F12' }}>
                    <div style={{ fontWeight: 800, marginBottom: 4 }}>🗓️ Unavailable dates</div>
                    {blockedRanges.slice(0, 4).map((r, i) => (
                      <div key={i}>{r.from} → {r.to} <span style={{ color: '#A16207' }}>({r.status})</span></div>
                    ))}
                    {blockedRanges.length > 4 && <div style={{ color: '#A16207' }}>+ {blockedRanges.length - 4} more…</div>}
                  </div>
                )}

                {localConflict && (
                  <div data-testid="conflict-warning" style={{ marginBottom: 12, padding: 10, background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 12, color: '#991B1B', fontWeight: 700 }}>
                    ⚠️ Your selected dates overlap with {localConflict.from} → {localConflict.to}. Please choose different dates.
                  </div>
                )}

                {days > 0 && (
                  <div data-testid="booking-summary" style={{ background: '#F1F5F9', borderRadius: 10, padding: 12, marginTop: 6, marginBottom: 12 }}>
                    <Row label={`${days} day${days > 1 ? 's' : ''} × $${car.ppd}`} value={`$${subtotal.toFixed(2)}`} />
                    <Row label="Deposit (refundable)" value={`$${car.dep.toFixed(2)}`} />
                    <div style={{ borderTop: '1px solid #CBD5E1', marginTop: 8, paddingTop: 8 }}>
                      <Row label="Total" value={`$${total.toFixed(2)}`} bold />
                    </div>
                  </div>
                )}

                <button type="submit" disabled={submitting || !!localConflict} className="btn-red" style={{ width: '100%', padding: '14px', marginTop: 4 }} data-testid="confirm-booking-btn">
                  {submitting ? 'Redirecting to payment…' : localConflict ? 'Dates unavailable' : `Pay $${total > 0 ? total.toFixed(2) : car.ppd.toFixed(2)} & Reserve`}
                </button>
                <p style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', marginTop: 10 }}>🔒 Secure checkout powered by Stripe — test mode.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Spec({ icon, label, value }) {
  return (
    <div className="card" style={{ padding: 14, textAlign: 'center' }}>
      <div style={{ fontSize: 20 }}>{icon}</div>
      <div style={{ fontWeight: 900, fontSize: 16, color: '#0F172A', marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', fontSize: 13 }}>
      <span style={{ color: '#64748B', fontWeight: bold ? 800 : 500 }}>{label}</span>
      <span style={{ color: '#0F172A', fontWeight: bold ? 900 : 700, fontSize: bold ? 16 : 13 }}>{value}</span>
    </div>
  );
}

function Inp({ label, value, onChange, type = 'text', required, testId, min }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>
        {label}
        {required && <span style={{ color: '#E63946' }}> *</span>}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        min={min}
        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13 }}
      />
    </div>
  );
}

function SelInp({ label, value, onChange, options, required, testId }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>
        {label}
        {required && <span style={{ color: '#E63946' }}> *</span>}
      </label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13, background: '#fff' }}
      >
        <option value="">-- Select --</option>
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </div>
  );
}
